import { sharedStyles } from '@lib/sharedStyles';
import { logError } from "@lib/logger";
import { CustomEventType } from "@/events/CustomEventRegistry";
import { QuestionType, QuestionTypeGerman } from "../../jeopardy/germanMapping";
import { IEventDataSource } from "@/types/interfaces";
import { RoundPointsFieldEnum } from "@/types/types";
import { broadcastJeopardyToRoom, resetJeopardyGame } from '@/features/jeopardy/jeopardyRoomHandlers';
import '@/features/room/JeopardyGridDisplay';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { QueueAndRunRequests, runRequestWithoutQueueing } from '@infrastructure/queueAndRunRequests';
import { UpdateRoundPointValue } from './types/updateRoundPointValue';

@customElement('jeopardy-moderator-panel')
export default class JeopardyModeratorPanel extends LitElement {
	static styles = [
		sharedStyles,
		css`
      .panel {
        width: 100%;
        box-sizing: border-box;
        padding: 16px;
        background: var(--color-pink);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .muted { color: #666; font-size: 0.9rem; }

      .controls-row {
        display: flex;
        gap: 8px;
        width: 100%;
        align-items: center;
      }

      .controls-row basic-select {
        flex: 1;
        min-width: 0;
      }

      .team-grid {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 8px;
        margin-bottom: 8px;
        direction: rtl;
      }

      .moderator-grid-container {
        width: 100%;
        min-width: 0;
        overflow: hidden;
        height: 400px;
      }

      .score-popup__buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 16px;
        margin-top: 20px;
      }

      .score-popup__status {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        color: #666;
      }

      .score-popup__spinner {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        border: var(--border-width) solid var(--color-sand);
        border-top-color: transparent;
        border-radius: 50%;
        animation: score-popup-spin 0.8s linear infinite;
      }

      @keyframes score-popup-spin {
        to { transform: rotate(360deg); }
      }
    `
	];

	@property({ type: Object }) event: any;
	@property({ type: Object }) game: any;
	@property({ type: String }) quizId: string | null = null;
	@property({ type: Object }) eventDataSource: IEventDataSource | null = null;
	@property({ attribute: false }) queueAndRunRequests: QueueAndRunRequests = runRequestWithoutQueueing;
	@property({ attribute: false }) updateRoundPointValue: UpdateRoundPointValue;

	@state() private quizData: any = null;
	@state() private loading = false;
	@state() private loadingList = false;
	@state() private quizList: { id: string; name: string }[] = [];
	@state() private selectedQuizId: string | null = null;
	@state() private showGrid: boolean = false;
	@state() private overlayQuestion: any | null = null;
	@state() private showOverlay: boolean = false;
	@state() private _usedCells: Set<string> = new Set();
	@state() private selectedTeamId: string | null = null;
	@state() private showScorePopup: boolean = false;
	@state() private isSubmittingScore: boolean = false;

	connectedCallback() {
		super.connectedCallback();
		if (this.quizId) {
			this.selectedQuizId = this.quizId;
			this._loadQuiz();
		} else {
			this._loadQuizList();
		}
	}

	updated(changed: Map<string, any>) {
		if (changed.has('quizId')) {
			if (this.quizId) {
				this.selectedQuizId = this.quizId;
				this._loadQuiz();
			}
		}
	}

	private async _loadQuizList() {
		this.loadingList = true;
		try {
			const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';
			const res = await fetch(`${API_BASE}/api/quizzes`);
			if (!res.ok) {
				logError('Failed to load quizzes', res.status);
				this.quizList = [];
				return;
			}
			const data = await res.json();
			const list = Array.isArray(data) ? data : (data.results ?? []);
			this.quizList = list
				.filter((q: any) => q.quiz_type && (q.quiz_type.name === 'Jeopardy' || (q.quiz_type.name && q.quiz_type.name.toLowerCase().includes('jeopardy'))))
				.map((q: any) => ({ id: q.id, name: q.name }));
		} catch (err) {
			logError('Error loading quiz list', err);
			this.quizList = [];
		} finally {
			this.loadingList = false;
		}
	}

	private async _loadQuiz() {
		if (!this.selectedQuizId) {
			this.quizData = null;
			return;
		}
		this.loading = true;
		try {
			const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';
			const res = await fetch(`${API_BASE}/api/quizzes/${encodeURIComponent(this.selectedQuizId)}`);
			if (!res.ok) {
				logError('Failed to load quiz', res.status);
				this.quizData = null;
				return;
			}
			this.quizData = await res.json();
		} catch (err) {
			logError('Error loading quiz', err);
			this.quizData = null;
		} finally {
			this.loading = false;
		}
	}

	private async _refreshGrid(): Promise<void> {
		await this._loadQuiz();
		if (this.quizData && this.showGrid) {
			this._broadcastToRoom('jeopardy-grid', { quiz: this.quizData });
		}
	}

	private _onSelectChange(e: CustomEvent) {
		const detail = e.detail || {};
		const newValue = detail.newValue ?? detail.value ?? null;
		this.selectedQuizId = newValue;
		if (this.selectedQuizId) {
			this._loadQuiz();
		}
	}

	private _broadcastToRoom(screenContent: string, extra: Record<string, any>): void {
		broadcastJeopardyToRoom(
			screenContent, extra, this.event, this.game,
			window.location.host, window.location.protocol === 'https:'
		);
	}

	private _handleStart() {
		if (!this.selectedQuizId || !this.quizData) return;
		this._broadcastToRoom('jeopardy-grid', { quiz: this.quizData });
		this.showGrid = true;
	}

	private _onGridCellClicked = (ev: CustomEvent) => {
		this._handleCellClick(ev.detail.categoryId, ev.detail.difficulty);
	};

	private async _handleCellClick(categoryId: string, difficultyId: number) {
		if (!this.quizData) return;
		if (!this.selectedTeamId) {
			alert('Bitte wähle zuerst ein Team aus!');
			return;
		}
		try {
			const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';
			const res = await fetch(`${API_BASE}/api/jeopardy/questions`);
			if (!res.ok) throw new Error(`Backend request failed: ${res.status}`);
			const data = await res.json();
			const all = Array.isArray(data) ? data : (data.results ?? []);
			const pool = (this.quizData.questions && this.quizData.questions.length > 0) ? this.quizData.questions : all;
			const candidates = (pool ?? []).filter((q: any) => q.category?.id === categoryId && q.difficulty === difficultyId);
			if (candidates.length === 0) {
				alert('Keine Fragen für diese Kategorie/Schwierigkeit vorhanden.');
				return;
			}
			const question = candidates[Math.floor(Math.random() * candidates.length)];

			this.overlayQuestion = question;
			this.showOverlay = true;

			this._broadcastToRoom('jeopardy-popup', { question });

			this._usedCells = new Set(this._usedCells).add(`${categoryId}::${difficultyId}`);

			try {
				if (this.quizData.questions) {
					this.quizData.questions = (this.quizData.questions ?? []).filter((q: any) => q.id !== question.id);
				}
				this.requestUpdate();
			} catch (err) {
				logError('Failed to remove used question from local quizData', err);
			}
		} catch (err) {
			logError('Fehler beim Abrufen der Fragen:', err);
			alert('Fehler beim Abrufen der Fragen. Bitte versuche es erneut.');
		}
	}

	private _closeOverlay = () => {
		this.showOverlay = false;
		this._broadcastToRoom('jeopardy-grid', {});
		if (this.selectedTeamId) {
			this.showScorePopup = true;
		}
	}

	private _revealAnswer = () => {
		this._broadcastToRoom('jeopardy-popup', { showAnswer: true, question: this.overlayQuestion });
	}

	private _currentRoundPoints(teamId: string, roundNumber: number): number {
		const gamePoint = this.event?.game_points?.find((gp: any) => gp.game === this.game.id && gp.team === teamId);
		const roundPoint = gamePoint?.rounds.find((round: any) => round.round === roundNumber);
		return roundPoint?.points ?? 0;
	}

	private async _handleScoreConfirm(correct: boolean): Promise<void> {
		if (this.isSubmittingScore) {
			return;
		}
		if (this.selectedTeamId && this.overlayQuestion) {
			const basePoints = this.overlayQuestion.difficulty || 0;
			const pointsToAdd = correct ? basePoints : -Math.floor(basePoints / 2);
			this.isSubmittingScore = true;
			try {
				const newValue = this._currentRoundPoints(this.selectedTeamId, 0) + pointsToAdd;
				await this.updateRoundPointValue(this.game.id, this.selectedTeamId, 0, RoundPointsFieldEnum.points, newValue);
				this._broadcastToRoom('jeopardy-grid', {});
			} catch (err) {
				logError('Failed to award jeopardy point', err);
				alert('Fehler beim Speichern der Punkte.');
			} finally {
				this.isSubmittingScore = false;
			}
		}
		this.showScorePopup = false;
		this.selectedTeamId = null;
		this.overlayQuestion = null;
	}

	private _handleResetGame = async (): Promise<void> => {
		this._usedCells = new Set();
		this.selectedTeamId = null;
		this.overlayQuestion = null;
		this.showOverlay = false;
		this.showScorePopup = false;

		if (this.event && this.game && this.eventDataSource) {
			try {
				const updatedEvent = await this.queueAndRunRequests(() => resetJeopardyGame(
					this.event, this.game,
					(eventId, gameId) => this.eventDataSource!.deleteGamePointsForGame(eventId, gameId),
					(eventId) => this.eventDataSource!.getEventById(eventId),
					(sc, ex) => this._broadcastToRoom(sc, ex)
				));
				this.dispatchEvent(new CustomEvent(CustomEventType.EVENT_UPDATED, {
					detail: { event: updatedEvent },
					bubbles: true,
					composed: true
				}));
			} catch (err) {
				logError('Failed to reset jeopardy game', err);
				alert('Fehler beim Zurücksetzen des Spiels.');
			}
		}

		this.requestUpdate();
	}

	render() {
		return html`
      <div class="panel">
        <h2>Jeopardy Moderator Panel</h2>

        <div class="controls-row">
          ${this.loadingList
            ? html`<span class="muted">Lade Quizzes…</span>`
            : this.quizList.length > 0
              ? html`<basic-select
                  .value=${this.selectedQuizId ?? ''}
                  .options=${this.quizList.map(q => ({ label: q.name, value: q.id }))}
                  @selection-change=${(e: CustomEvent) => this._onSelectChange(e)}
                ></basic-select>`
              : html`<span class="muted">Keine Jeopardy-Quizzes gefunden</span>`
          }
          <!-- <basic-button @click=${() => this._refreshGrid()}>Aktualisieren</basic-button> -->
          <success-button @click=${this._handleStart} ?disabled=${!(this.selectedQuizId && this.quizData)}>Starte Quiz</success-button>
          ${this.showGrid ? html`<alert-button @click=${this._handleResetGame}>Spiel zurücksetzen</alert-button>` : nothing}
        </div>

        ${this.showGrid && this.quizData ? html`
          <div class="team-grid">
            ${[...(this.event?.teams ?? [])].sort((a, b) => a.team_number - b.team_number).map((team: any) => html`
              <basic-button ?active=${this.selectedTeamId === team.id} @click=${() => this.selectedTeamId = team.id}>
                <span style="display:flex; align-items:center; gap:6px; direction:ltr;">
                  <span style="width:16px; height:16px; flex-shrink:0;">
                    <team-color-swatch .color=${team.color}></team-color-swatch>
                  </span>
                  ${team.name}
                </span>
              </basic-button>
            `)}
          </div>

          <div class="moderator-grid-container" style="opacity:${this.selectedTeamId ? 1 : 0.4}; pointer-events:${this.selectedTeamId ? 'auto' : 'none'};">
            <jeopardy-grid-display
              .quiz=${this.quizData}
              .usedCells=${this._usedCells}
              @jeopardy-cell-clicked=${this._onGridCellClicked}
            ></jeopardy-grid-display>
          </div>
        ` : nothing}

        ${this.showOverlay && this.overlayQuestion ? html`
          <div style="position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:99999; display:flex; align-items:center; justify-content:center;">
            <div style="background:#fff; padding:20px; border-radius:8px; max-width:800px; width:90%; box-shadow:0 10px 30px rgba(0,0,0,0.3);">
              <h3 style="margin:0 0 6px 0;">${QuestionTypeGerman[this.overlayQuestion.question_type as QuestionType] ?? 'Frage'}</h3>
              <div style="color:#666; font-size:0.9rem; margin-bottom:10px;">Kategorie: ${this.overlayQuestion.category?.name ?? '-'} · Schwierigkeit: ${this.overlayQuestion.difficulty ?? '-'}</div>
              <div style="font-size:1.1rem; margin-bottom:10px;">${this.overlayQuestion.question}</div>
              <div style="font-weight:600;">Antwort: ${this.overlayQuestion.answer}</div>
              <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:12px;">
                <button @click=${this._revealAnswer} style="padding:8px 12px; border:1px solid #ddd; border-radius:6px; background:#e0f4af; cursor:pointer;">Antwort anzeigen</button>
                <button @click=${this._closeOverlay} style="padding:8px 12px; border:1px solid #ddd; border-radius:6px; background:#f9f9f9; cursor:pointer;">Schließen</button>
              </div>
            </div>
          </div>
        ` : nothing}

        ${this.showScorePopup ? html`
          <div style="position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:99999; display:flex; align-items:center; justify-content:center;">
            <div style="background:#fff; padding:20px; border-radius:8px; max-width:400px; width:90%; box-shadow:0 10px 30px rgba(0,0,0,0.3); text-align:center;">
              <h3 style="margin-top:0;">Hat das Team richtig geantwortet?</h3>
              <div class="score-popup__buttons">
                <success-button ?disabled=${this.isSubmittingScore} @click=${() => this._handleScoreConfirm(true)}>JA (+${this.overlayQuestion?.difficulty || 0} Punkte)</success-button>
                <alert-button ?disabled=${this.isSubmittingScore} @click=${() => this._handleScoreConfirm(false)}>NEIN (-${Math.floor((this.overlayQuestion?.difficulty || 0) / 2)} Punkte)</alert-button>
              </div>
              ${this.isSubmittingScore ? html`
                <div class="score-popup__status">
                  <span class="score-popup__spinner"></span>
                  <span>Punkte werden gespeichert…</span>
                </div>
              ` : nothing}
            </div>
          </div>
        ` : nothing}
       </div>
     `;
	}
}
